import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { HardHat, MapPin, Clock, Briefcase, CheckCircle, PaperPlaneTilt, Paperclip, X, Translate } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RESUME_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_MIME = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];

/** Detecta idioma inicial: parámetro ?lang= > localStorage > navigator > 'es'. */
const detectInitialLang = () => {
  try {
    const url = new URL(window.location.href);
    const q = (url.searchParams.get("lang") || "").toLowerCase();
    if (q === "en" || q === "es") return q;
    const stored = localStorage.getItem("careers_lang");
    if (stored === "en" || stored === "es") return stored;
    const nav = (navigator.language || "es").toLowerCase();
    return nav.startsWith("en") ? "en" : "es";
  } catch {
    return "es";
  }
};

/** Iter133 · Lee `?job={vacancy_id}` de la URL (viene del QR del PDF de
 * postulación) para auto-abrir el diálogo de la vacante correspondiente. */
const detectJobParam = () => {
  try {
    return new URL(window.location.href).searchParams.get("job") || "";
  } catch { return ""; }
};

/** Etiquetas UI localizadas del formulario de postulación. */
const T = {
  es: {
    apply: "Postular",
    openPositions: "Vacantes abiertas",
    noVacancies: "Sin vacantes publicadas por ahora. Vuelve pronto.",
    thankTitle: "¡Gracias!",
    thankSubtitle: "Recibimos tu postulación. Nuestro equipo se pondrá en contacto.",
    close: "Cerrar",
    fullName: "Nombre completo",
    email: "Correo electrónico",
    phone: "Teléfono",
    location: "Ubicación",
    experience: "Años de experiencia",
    expected: "Sueldo esperado",
    currency: "Moneda",
    resume: "CV / Currículum",
    resumeHint: "PDF, JPG, PNG o WEBP · máx 5MB.",
    upload: "Adjuntar archivo",
    message: "Mensaje adicional (opcional)",
    send: "Enviar postulación",
    sending: "Enviando...",
    langSwitch: "English",
    postedOn: "Publicada",
    responsibilities: "Responsabilidades",
    requirements: "Requisitos",
  },
  en: {
    apply: "Apply",
    openPositions: "Open positions",
    noVacancies: "No positions posted right now. Please check back soon.",
    thankTitle: "Thank you!",
    thankSubtitle: "We received your application. Our team will be in touch.",
    close: "Close",
    fullName: "Full name",
    email: "Email",
    phone: "Phone",
    location: "Location",
    experience: "Years of experience",
    expected: "Expected salary",
    currency: "Currency",
    resume: "Resume / CV",
    resumeHint: "PDF, JPG, PNG or WEBP · max 5MB.",
    upload: "Attach file",
    message: "Additional message (optional)",
    send: "Submit application",
    sending: "Sending...",
    langSwitch: "Español",
    postedOn: "Posted",
    responsibilities: "Responsibilities",
    requirements: "Requirements",
  },
};

const emptyApp = () => ({
  name: "", email: "", phone: "", location: "", experience_years: "",
  expected_salary: "", currency: "MXN", resume_url: "", message: "",
  resume_file: "", resume_filename: "", resume_content_type: "",
});

const readAsDataURL = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function Careers() {
  const [vacancies, setVacancies] = useState(null);
  const [logo, setLogo] = useState(null);
  const [lang, setLang] = useState(detectInitialLang);
  const [hero, setHero] = useState({
    title: "Trabaja con Nosotros",
    subtitle: "Únete a nuestro equipo de minería subterránea. Postula directamente a las vacantes abiertas y nuestro equipo de reclutamiento te contactará.",
    title_en: "Work With Us",
    subtitle_en: "Join our underground mining team. Apply directly to our open positions and our recruitment team will get in touch.",
    bg_color: "#0c183c",
    bg_image: "",
  });
  const [applying, setApplying] = useState(null);
  const [form, setForm] = useState(emptyApp());
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);
  const t = T[lang];

  useEffect(() => {
    try { localStorage.setItem("careers_lang", lang); } catch { /* noop */ }
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    axios.get(`${API}/careers/vacancies`)
      .then((r) => {
        setVacancies(r.data.vacancies);
        setLogo(r.data.company_logo);
        setHero({
          title: r.data.hero_title || "Trabaja con Nosotros",
          subtitle: r.data.hero_subtitle || "",
          title_en: r.data.hero_title_en || "Work With Us",
          subtitle_en: r.data.hero_subtitle_en || "",
          bg_color: r.data.hero_bg_color || "#0c183c",
          bg_image: r.data.hero_bg_image || "",
        });
        // Iter133 · si venimos del QR con ?job=xxx, auto-abrir el diálogo
        const jobId = detectJobParam();
        if (jobId) {
          const match = (r.data.vacancies || []).find((v) => v.id === jobId);
          if (match) {
            setApplying(match);
            setTimeout(() => {
              const el = document.querySelector(`[data-vacancy-id="${jobId}"]`);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 300);
          } else {
            toast.info(lang === "en"
              ? "This position is no longer available. Browse other openings below."
              : "Esta vacante ya no está disponible. Revisa las demás abiertas.");
          }
        }
      })
      .catch(() => setVacancies([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      toast.error("Formato no soportado. Sube PDF o imagen (JPG/PNG/WEBP).");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_RESUME_BYTES) {
      toast.error("El archivo excede 5 MB. Comprime tu PDF o sube una imagen.");
      e.target.value = "";
      return;
    }
    try {
      const data = await readAsDataURL(file);
      setForm((f) => ({ ...f, resume_file: data, resume_filename: file.name, resume_content_type: file.type }));
      toast.success(`Archivo cargado: ${file.name}`);
    } catch {
      toast.error("No se pudo leer el archivo");
    }
  };

  const clearFile = () => {
    setForm((f) => ({ ...f, resume_file: "", resume_filename: "", resume_content_type: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const apply = async () => {
    if (form.name.trim().length < 3) { toast.error("Ingrese su nombre completo"); return; }
    if (!form.email.trim() && !form.phone.trim()) { toast.error("Indique correo o teléfono"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/careers/${applying.id}/apply`, {
        ...form,
        experience_years: Number(form.experience_years || 0),
        expected_salary: Number(form.expected_salary || 0),
      });
      setSent(true);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo enviar la postulación");
    } finally { setBusy(false); }
  };

  const closeDialog = () => { setApplying(null); setForm(emptyApp()); setSent(false); if (fileRef.current) fileRef.current.value = ""; };

  return (
    <div className="min-h-screen bg-slate-50">
      <header
        className="relative text-white overflow-hidden"
        style={{
          backgroundColor: hero.bg_color || "#0c183c",
          backgroundImage: hero.bg_image ? `url(${hero.bg_image})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="careers-hero"
      >
        {hero.bg_image && (
          <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
        )}
        <div className="max-w-5xl mx-auto px-6 py-12 relative">
          <div className="flex items-center gap-3 mb-6">
            {logo ? (
              <img src={logo} alt="logo" className="h-10 max-w-[140px] object-contain bg-white rounded p-1" />
            ) : (
              <div className="h-10 w-10 rounded-md bg-blue-900 flex items-center justify-center">
                <HardHat size={22} weight="fill" className="text-amber-400" />
              </div>
            )}
            <div className="leading-tight">
              <div className="font-heading font-black text-lg">UG <span className="text-amber-400">Managment</span></div>
              <div className="text-[10px] uppercase tracking-widest text-blue-200">by OOMINING</div>
            </div>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight" data-testid="careers-hero-title">
            {lang === "en" ? hero.title_en : hero.title}
          </h1>
          <p className="text-blue-100 mt-3 max-w-xl text-sm whitespace-pre-line" data-testid="careers-hero-subtitle">
            {lang === "en" ? hero.subtitle_en : hero.subtitle}
          </p>
          <button
            type="button"
            data-testid="careers-lang-toggle"
            onClick={() => setLang(lang === "en" ? "es" : "en")}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/25 text-white text-xs font-bold rounded-full px-3 py-1.5 transition"
            aria-label="Change language"
          >
            <Translate size={13} weight="bold" /> {t.langSwitch}
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {vacancies === null ? (
          <p className="text-sm text-slate-400 text-center py-16">Cargando vacantes…</p>
        ) : vacancies.length === 0 ? (
          <div className="text-center py-16" data-testid="careers-empty">
            <Briefcase size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-semibold">No hay vacantes abiertas por el momento.</p>
            <p className="text-xs text-slate-400 mt-1">Vuelve a revisar pronto.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2" data-testid="careers-list">
            {vacancies.map((v) => (
              <VacancyCard key={v.id} v={v} lang={lang} onApply={() => setApplying(v)} />
            ))}
          </div>
        )}
      </main>

      <footer className="text-center text-[11px] text-slate-400 pb-8">
        UG Managment by OOMINING · Sistema de gestión de proyectos mineros subterráneos
      </footer>

      <Dialog open={!!applying} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          {sent ? (
            <div className="text-center py-8" data-testid="careers-success">
              <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-500 mb-3" />
              <h3 className="font-heading font-bold text-xl text-slate-900">¡Postulación enviada!</h3>
              <p className="text-sm text-slate-500 mt-2">
                Gracias por tu interés en <b>{applying?.title}</b>. Nuestro equipo de reclutamiento
                revisará tu perfil y te contactará si avanzas en el proceso.
              </p>
              <Button onClick={closeDialog} className="bg-blue-900 hover:bg-blue-800 mt-5" data-testid="careers-success-close">Cerrar</Button>
            </div>
          ) : (
            <>
              <DialogHeader><DialogTitle className="font-heading">Postular a: {applying?.title}</DialogTitle></DialogHeader>
              {applying && <VacancyDetailBrief v={applying} />}
              <div className="space-y-3 border-t border-slate-200 pt-4">
                <div><Label className="overline-label">Nombre completo *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="careers-form-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="overline-label">Correo</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="careers-form-email" /></div>
                  <div><Label className="overline-label">Teléfono</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="careers-form-phone" /></div>
                  <div><Label className="overline-label">Ciudad / ubicación</Label>
                    <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ej. Sonora, México" data-testid="careers-form-location" /></div>
                  <div><Label className="overline-label">Años de experiencia</Label>
                    <Input type="number" min="0" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} data-testid="careers-form-exp" /></div>
                  <div><Label className="overline-label">Expectativa salarial (MXN)</Label>
                    <Input type="number" min="0" value={form.expected_salary} onChange={(e) => setForm({ ...form, expected_salary: e.target.value })} data-testid="careers-form-salary" /></div>
                  <div><Label className="overline-label">Link a tu CV (opcional)</Label>
                    <Input value={form.resume_url} onChange={(e) => setForm({ ...form, resume_url: e.target.value })} placeholder="Drive, Dropbox…" data-testid="careers-form-cv" /></div>
                </div>
                <div>
                  <Label className="overline-label">Adjuntar CV o solicitud (PDF o imagen · máx 5MB)</Label>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,image/png,image/jpeg,image/jpg,image/webp,application/pdf"
                      onChange={onFile}
                      className="text-xs text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-900 hover:file:bg-blue-100"
                      data-testid="careers-form-file"
                    />
                    {form.resume_file && (
                      <span className="inline-flex items-center gap-1.5 text-[11px] bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-1 rounded">
                        <Paperclip size={11} weight="bold" /> {form.resume_filename}
                        <button type="button" onClick={clearFile} className="hover:text-red-600" data-testid="careers-form-file-clear"><X size={11} weight="bold" /></button>
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">Formatos aceptados: PDF, JPG, PNG, WEBP.</p>
                </div>
                <div><Label className="overline-label">Mensaje (opcional)</Label>
                  <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                            placeholder="Cuéntanos brevemente tu experiencia relevante…" data-testid="careers-form-message" /></div>
                <Button onClick={apply} disabled={busy} className="bg-blue-900 hover:bg-blue-800 w-full gap-2" data-testid="careers-form-submit">
                  <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Enviando…" : "Enviar postulación"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Iter134d · Card compacta con "Ver más" para expandir la ficha completa.
// Antes se cortaba el summary a 3 líneas y solo se listaban 3 responsabilidades
// — el postulante no podía leer la descripción sin abrir el diálogo.
function VacancyCard({ v, lang, onApply }) {
  const [expanded, setExpanded] = useState(false);
  const responsibilities = v.responsibilities || [];
  const hasMoreDetail = responsibilities.length > 3
    || (v.summary && v.summary.length > 180)
    || (v.must_have || []).length > 0
    || (v.benefits || []).length > 0;
  return (
    <div data-vacancy-id={v.id}
          className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col hover:shadow-md transition-shadow"
          data-testid={`careers-vacancy-${v.id}`}>
      <div className="flex items-start justify-between gap-2">
        <h2 className="font-heading font-bold text-lg text-slate-900">{v.title}</h2>
        {v.priority === "critical" && (
          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-red-100 text-red-700 shrink-0">Urgente</span>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-1">{v.department}</p>
      <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-slate-500">
        {v.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {v.location}</span>}
        {v.regime && <span className="inline-flex items-center gap-1"><Clock size={12} /> Régimen {v.regime}</span>}
        {v.contract_type && <span className="inline-flex items-center gap-1"><Briefcase size={12} /> {v.contract_type}</span>}
      </div>
      {v.summary && (
        <p className={`text-xs text-slate-600 mt-3 whitespace-pre-line ${expanded ? "" : "line-clamp-3"}`}>
          {v.summary}
        </p>
      )}
      {responsibilities.length > 0 && (
        <ul className="text-[11px] text-slate-500 mt-2 space-y-0.5 list-disc pl-4"
             data-testid={`careers-responsibilities-${v.id}`}>
          {(expanded ? responsibilities : responsibilities.slice(0, 3))
            .map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
      {expanded && (
        <div className="mt-2 space-y-2 text-[11px] text-slate-500">
          {(v.must_have || []).length > 0 && (
            <div>
              <div className="font-bold uppercase tracking-widest text-slate-600 mt-2">{lang === "en" ? "Requirements" : "Requisitos"}</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {v.must_have.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {(v.nice_to_have || []).length > 0 && (
            <div>
              <div className="font-bold uppercase tracking-widest text-slate-600 mt-2">{lang === "en" ? "Nice to have" : "Deseable"}</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {v.nice_to_have.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {(v.benefits || []).length > 0 && (
            <div>
              <div className="font-bold uppercase tracking-widest text-slate-600 mt-2">{lang === "en" ? "Benefits" : "Beneficios"}</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {v.benefits.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {(v.education || v.experience_years > 0 || v.salary_min > 0) && (
            <div className="pt-1 flex flex-wrap gap-2">
              {v.experience_years > 0 && (
                <span className="inline-flex bg-slate-100 text-slate-700 rounded px-2 py-0.5">
                  {lang === "en" ? "Experience" : "Experiencia"}: {v.experience_years}+ {lang === "en" ? "years" : "años"}
                </span>
              )}
              {v.education && (
                <span className="inline-flex bg-slate-100 text-slate-700 rounded px-2 py-0.5">
                  {lang === "en" ? "Education" : "Educación"}: {v.education}
                </span>
              )}
              {v.salary_min > 0 && v.salary_max > 0 && (
                <span className="inline-flex bg-emerald-100 text-emerald-800 rounded px-2 py-0.5 font-bold">
                  ${v.salary_min.toLocaleString()} – ${v.salary_max.toLocaleString()} {v.currency || "MXN"}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      {hasMoreDetail && (
        <button type="button"
                 onClick={() => setExpanded((x) => !x)}
                 className="text-[11px] text-blue-800 hover:text-blue-900 font-bold mt-2 self-start"
                 data-testid={`careers-toggle-${v.id}`}>
          {expanded ? "▲ Ver menos" : "▼ Ver descripción completa"}
        </button>
      )}
      <div className="flex-1" />
      <Button onClick={onApply} data-testid={`careers-apply-${v.id}`}
              className="bg-blue-900 hover:bg-blue-800 mt-4 w-full gap-2">
        <PaperPlaneTilt size={14} weight="bold" /> Postular
      </Button>
    </div>
  );
}


// Iter134d · Ficha completa mostrada arriba del formulario de postulación
// para que el candidato la tenga a la vista mientras llena sus datos.
function VacancyDetailBrief({ v }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-2 max-h-[45vh] overflow-y-auto"
          data-testid="careers-vacancy-brief">
      <div className="flex flex-wrap gap-2 text-[10px] text-slate-500 mb-2">
        {v.department && <span className="bg-white border border-slate-200 rounded px-2 py-0.5">{v.department}</span>}
        {v.location && <span className="bg-white border border-slate-200 rounded px-2 py-0.5">📍 {v.location}</span>}
        {v.regime && <span className="bg-white border border-slate-200 rounded px-2 py-0.5">⏱ {v.regime}</span>}
        {v.contract_type && <span className="bg-white border border-slate-200 rounded px-2 py-0.5">📄 {v.contract_type}</span>}
        {v.experience_years > 0 && <span className="bg-white border border-slate-200 rounded px-2 py-0.5">💼 {v.experience_years}+ años</span>}
      </div>
      {v.summary && (
        <p className="text-xs text-slate-700 whitespace-pre-line mb-2">{v.summary}</p>
      )}
      {(v.responsibilities || []).length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Responsabilidades</div>
          <ul className="list-disc pl-4 text-xs text-slate-700 space-y-0.5">
            {v.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      {(v.must_have || []).length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Requisitos</div>
          <ul className="list-disc pl-4 text-xs text-slate-700 space-y-0.5">
            {v.must_have.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      {(v.nice_to_have || []).length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Deseable</div>
          <ul className="list-disc pl-4 text-xs text-slate-700 space-y-0.5">
            {v.nice_to_have.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      {(v.benefits || []).length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Beneficios</div>
          <ul className="list-disc pl-4 text-xs text-slate-700 space-y-0.5">
            {v.benefits.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      {(v.education || v.salary_min > 0) && (
        <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
          {v.education && (
            <span className="bg-white border border-slate-200 rounded px-2 py-0.5 text-slate-700">
              🎓 {v.education}
            </span>
          )}
          {v.salary_min > 0 && v.salary_max > 0 && (
            <span className="bg-emerald-100 text-emerald-800 rounded px-2 py-0.5 font-bold">
              💰 ${v.salary_min.toLocaleString()} – ${v.salary_max.toLocaleString()} {v.currency || "MXN"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
